/**
 * BOGOS (gift-with-purchase) bridge for Horizon native cart drawer.
 * BOGOS may mutate the cart shortly after /cart/add or /cart/change returns.
 * This module re-fetches /cart.js after theme cart events and re-renders
 * cart-items sections when the live cart diverges from the last known snapshot.
 *
 * BOGOS promotions are applied by the BOGOS app scripts (theme app embed). If
 * nothing happens when adding to cart, turn on the BOGOS embed under Online Store
 * → Themes → Customize → App embeds for this theme.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

function getCartJsonUrl() {
  const base =
    typeof Theme !== 'undefined' && Theme?.routes?.cart_url ? Theme.routes.cart_url : '/cart';
  const normalized = base.endsWith('/') ? `${base.slice(0, -1)}.js` : `${base}.js`;
  return normalized;
}

/** @param {Record<string, unknown> | null | undefined} cart */
function cartSignature(cart) {
  if (!cart || !Array.isArray(cart.items)) return '';
  const lines = cart.items
    .map((item) => `${item.key ?? ''}:${item.quantity ?? 0}`)
    .join('|');
  return `${cart.item_count ?? 0}:${cart.total_price ?? 0}:${lines}`;
}

function getCartItemsSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  }
  return ids;
}

/** @type {string} */
let lastKnownSignature = '';

/** @type {ReturnType<typeof setTimeout> | null} */
let debouncePrimary = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let debounceSecondary = null;

/** Primary wait — BOGOS often finishes within this window. */
const DEBOUNCE_MS = 550;

/** Secondary pass — catches slower gift application after AJAX cart. */
const FOLLOW_UP_MS = 1600;

async function refreshCartSections() {
  const sectionIds = getCartItemsSectionIds();
  if (sectionIds.size === 0) return;

  for (const sectionId of sectionIds) {
    const host = document.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`);
    const mode =
      host instanceof HTMLElement && host.dataset.drawer !== undefined ? 'hydration' : 'full';
    await sectionRenderer.renderSection(sectionId, { cache: false, mode });
  }
}

async function syncAfterCartActivity() {
  let cart;
  try {
    const res = await fetch(getCartJsonUrl(), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    cart = await res.json();
  } catch {
    return;
  }

  const sig = cartSignature(cart);
  if (sig === lastKnownSignature) return;

  await refreshCartSections();

  lastKnownSignature = sig;

  document.dispatchEvent(
    new CartUpdateEvent(cart, 'bogos-cart-sync', {
      itemCount: cart.item_count,
      source: 'bogos-cart-sync',
      suppressSectionMorph: true,
    })
  );
}

function scheduleSync() {
  if (debouncePrimary) clearTimeout(debouncePrimary);
  if (debounceSecondary) clearTimeout(debounceSecondary);

  debouncePrimary = setTimeout(() => {
    debouncePrimary = null;
    syncAfterCartActivity();
  }, DEBOUNCE_MS);

  debounceSecondary = setTimeout(() => {
    debounceSecondary = null;
    syncAfterCartActivity();
  }, FOLLOW_UP_MS);
}

/** @param {CustomEvent} event */
function onCartUpdate(event) {
  if (event.detail?.data?.suppressSectionMorph) return;

  const cart = event.detail?.resource;
  if (cart && Array.isArray(cart.items)) {
    lastKnownSignature = cartSignature(cart);
  }

  scheduleSync();
}

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);

document.addEventListener(ThemeEvents.discountUpdate, scheduleSync);

for (const name of ['bogos:cart-updated', 'bogos:cart:updated', 'free-gift:updated']) {
  document.addEventListener(name, scheduleSync, { passive: true });
}
